
import React, { useMemo, useState } from 'react';
import { CircleMarker, Popup } from 'react-leaflet';
import { Coordinates, CampingSpot, WaypointContent } from '../../types';
import { getDistance, isPointNearPolyline, getPolylineBbox } from '../../services/geometryUtils';

import { BRAND } from '../../constants/mapColors';
interface PhotoPoint {
  id: string;
  name: string;
  location: Coordinates;
  photoUrl: string;
  source: 'spot' | 'waypoint';
  distanceAlongRouteM?: number;
}

interface TrailPhotoLayerProps {
  routeCoords: Coordinates[];
  spots: CampingSpot[];
  waypoints?: WaypointContent[];
  zoom: number;
}

const PROXIMITY_M = 200;
const MIN_ZOOM = 12;
const CLUSTER_DISTANCE_M = 100; // cluster photos within 100m of each other

function clusterPhotos(points: PhotoPoint[]): PhotoPoint[] {
  if (points.length <= 1) return points;
  const used = new Set<string>();
  const clustered: PhotoPoint[] = [];

  for (const p of points) {
    if (used.has(p.id)) continue;
    used.add(p.id);
    // This point becomes the representative; skip nearby ones
    for (const q of points) {
      if (used.has(q.id)) continue;
      const d = getDistance(
        p.location.latitude, p.location.longitude,
        q.location.latitude, q.location.longitude,
      );
      if (d < CLUSTER_DISTANCE_M) {
        used.add(q.id);
      }
    }
    clustered.push(p);
  }
  return clustered;
}

const TrailPhotoLayer: React.FC<TrailPhotoLayerProps> = ({
  routeCoords,
  spots,
  waypoints = [],
  zoom,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const photoPoints = useMemo((): PhotoPoint[] => {
    if (zoom < MIN_ZOOM || routeCoords.length < 2) return [];

    const bbox = getPolylineBbox(routeCoords);
    const points: PhotoPoint[] = [];

    // Spots with photos near route
    for (const s of spots) {
      const photoUrl = s.photoUrls?.[0] || s.auto_image_url;
      if (!photoUrl) continue;
      const loc = s.location;
      if (isPointNearPolyline(loc, routeCoords, PROXIMITY_M, bbox)) {
        points.push({
          id: `spot-${s.id}`,
          name: s.name || 'Ukjent plass',
          location: loc,
          photoUrl,
          source: 'spot',
        });
      }
    }

    // User waypoints with photos near route
    for (const wp of waypoints) {
      if (!wp.photoUrl) continue;
      const loc: Coordinates = { latitude: wp.latitude, longitude: wp.longitude };
      if (isPointNearPolyline(loc, routeCoords, PROXIMITY_M, bbox)) {
        points.push({
          id: `wp-${wp.id}`,
          name: wp.name,
          location: loc,
          photoUrl: wp.photoUrl,
          source: 'waypoint',
        });
      }
    }

    return clusterPhotos(points);
  }, [routeCoords, spots, waypoints, zoom]);

  if (photoPoints.length === 0) return null;

  return (
    <>
      {photoPoints.map(p => (
        <CircleMarker
          key={p.id}
          center={[p.location.latitude, p.location.longitude]}
          radius={zoom >= 14 ? 8 : 6}
          pathOptions={{
            color: BRAND,
            fillColor: BRAND,
            fillOpacity: 0.45,
            weight: 2,
            opacity: 0.7,
          }}
          eventHandlers={{
            click: () => setExpandedId(p.id),
          }}
        >
          <Popup>
            <div className="min-w-[160px] max-w-[200px]">
              <div className="w-full h-28 rounded-lg overflow-hidden mb-1.5 -mt-1">
                <img
                  src={p.photoUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-tiny font-bold text-stone-800 truncate">{p.name}</p>
              <p className="text-3xs text-stone-400 mt-0.5">
                {p.source === 'waypoint' ? 'Eget waypoint' : 'Fricamp-plass'}
              </p>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
};

export default TrailPhotoLayer;
